<template>
  <div class="space-y-8">
    <!-- Project Details Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

            <h3 class="text-lg font-semibold text-gray-900">
              {{ $t('ProjectSettings.ProjectTab.sections.details') }}
            </h3>
          </div>

          <button
            v-if="projectsStore.project?.unarchived"
            type="button"
            class="btn btn--primary-blue flex items-center space-x-2"
            data-test="save-project"
            @click="updateProject"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

            <span>{{ $t('ProjectSettings.ProjectTab.buttons.saveProject') }}</span>
          </button>
        </div>
      </div>

      <div class="p-6">
        <form
          class="space-y-8"
          @submit.prevent="updateProject"
        >
          <!-- Basic Information Section -->
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div class="space-y-6">
              <div>
                <h4 class="text-sm font-medium text-gray-900 mb-4 flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"  class="w-4 h-4 text-gray-500">
    <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
</svg>

                  <span>Basic Information</span>
                </h4>

                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      {{ $t('ProjectSettings.ProjectTab.labels.name') }}
                    </label>

                    <AppInput
                      v-model="projectForm.form.name"
                      name="name"
                      type="text"
                      data-test="name"
                      class="form-input"
                      :error="projectForm.getErrors('name')"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      {{ $t('ProjectSettings.ProjectTab.labels.reference') }}
                    </label>


                    <AppInput
                      v-model="projectForm.form.reference"
                      name="reference"
                      type="text"
                      data-test="reference"
                      class="form-input"
                      :error="projectForm.getErrors('reference')"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      {{ $t('ProjectSettings.ProjectTab.labels.locale') }}
                    </label>


                    <AppSelect
                      v-model="projectForm.form.locale"
                      :options="$i18n.availableLocales.map((locale) => {
                        return {
                          label: $t(`currency.titles.${locale}`),
                          value: locale
                        }
                      })"
                      name="locale"
                      data-test="locale"
                      class="form-input"
                      :error="projectForm.getErrors('locale')"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Business Role for Project
                    </label>

                    <AppInput
                      v-model="projectForm.form.business_role"
                      name="business_role"
                      type="text"
                      data-test="business_role"
                      class="form-input"
                      :error="projectForm.getErrors('business_role')"
                    />
                  </div>

                  <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p class="text-sm font-medium text-gray-700">
                        {{ $t('ProjectSettings.ProjectTab.labels.trackWeekends') }}
                      </p>

                      <p class="text-xs text-gray-500">
                        Include weekends in utilisation calculations
                      </p>
                    </div>

                    <input
                      v-model="projectForm.form.track_weekends"
                      type="checkbox"
                      name="track_weekends"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    >
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      {{ $t('ProjectSettings.ProjectTab.labels.defaultDashboard') }}
                    </label>

                    <AppSelect
                      v-model="projectForm.form.default_dashboard"
                      name="default_dashboard"
                      :options="[{ value: 'head_contract', label: 'Head Contract'}, { value: 'self_perform', label: 'Self Perform'}]"
                      class="form-input"
                      :error="projectForm.getErrors('default_dashboard')"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="space-y-6">
              <div>
                <h4 class="text-sm font-medium text-gray-900 mb-4 flex items-center space-x-2">
                  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-500">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 6h16M4 12h16M4 18h7"
    />
</svg>

                  <span>Description</span>
                </h4>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    {{ $t('ProjectSettings.ProjectTab.labels.description') }}
                  </label>

                  <textarea
                    v-model="projectForm.form.description"
                    name="description"
                    class="form-input resize-none"
                    rows="8"
                    placeholder="Enter project description..."
                  />

                  <span
                    v-if="projectForm.getErrors('description')"
                    class="text-red-600 text-sm mt-1 block"
                  >{{ projectForm.getErrors('description') }}</span>
                </div>
              </div>
            </div>

            <div class="space-y-6">
              <div>
                <h4 class="text-sm font-medium text-gray-900 mb-4 flex items-center space-x-2">
                  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    stroke-width="2"
 class="w-4 h-4 text-gray-500">
    <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
    />

    <path
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 6v6l4 2"
    />
</svg>

                  <span>Shift Configuration</span>
                </h4>

                <div class="space-y-4">
                  <div class="p-4 bg-gray-50 rounded-lg">
                    <h5 class="text-sm font-medium text-gray-900 mb-3">
                      {{ $t('ProjectSettings.ProjectTab.sections.standardShift') }}
                    </h5>

                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-600 mb-1">
                          {{ $t('ProjectSettings.ProjectTab.labels.labour') }} <span class="text-sm text-gray-400">({{ $t('ProjectSettings.ProjectTab.units.hours') }})</span>
                        </label>

                        <AppInput
                          v-model.number="projectForm.form.labour_standard_shift"
                          name="labour_standard_shift"
                          step=".5"
                          type="number"
                          data-test="labour_standard_shift"
                          class="form-input"
                          :error="projectForm.getErrors('labour_standard_shift')"
                        />
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-600 mb-1">
                          {{ $t('ProjectSettings.ProjectTab.labels.plant') }} <span class="text-sm text-gray-400">({{ $t('ProjectSettings.ProjectTab.units.hours') }})</span>
                        </label>

                        <AppInput
                          v-model.number="projectForm.form.plant_standard_shift"
                          name="plant_standard_shift"
                          step=".5"
                          type="number"
                          data-test="plant_standard_shift"
                          class="form-input"
                          :error="projectForm.getErrors('plant_standard_shift')"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="p-4 bg-gray-50 rounded-lg">
                    <h5 class="text-sm font-medium text-gray-900 mb-3">
                      Shift Times
                    </h5>

                    <div class="space-y-3">
                      <div>
                        <label class="block text-sm font-medium text-gray-600 mb-1">
                          {{ $t('ProjectSettings.ProjectTab.labels.maximum') }} <span class="text-sm text-gray-400">({{ $t('ProjectSettings.ProjectTab.units.hours') }})</span>
                        </label>

                        <AppInput
                          v-model.number="projectForm.form.maximum_shift"
                          name="maximum_shift"
                          type="number"
                          step=".5"
                          data-test="maximum_shift"
                          class="form-input"
                          :error="projectForm.getErrors('maximum_shift')"
                        />
                      </div>

                      <div class="grid grid-cols-2 gap-3">
                        <div>
                          <label class="block text-sm font-medium text-gray-600 mb-1">
                            {{ $t('ProjectSettings.ProjectTab.labels.start') }}
                          </label>

                          <AppInput
                            v-model="projectForm.form.shift_start_at"
                            name="shift_start_at"
                            type="time"
                            data-test="shift_start_at"
                            class="form-input"
                            :error="projectForm.getErrors('shift_start_at')"
                          />
                        </div>

                        <div>
                          <label class="block text-sm font-medium text-gray-600 mb-1">
                            {{ $t('ProjectSettings.ProjectTab.labels.nightStart') }}
                          </label>

                          <AppInput
                            v-model="projectForm.form.night_shift_start_at"
                            name="night_shift_start_at"
                            type="time"
                            data-test="night_shift_start_at"
                            class="form-input"
                            :error="projectForm.getErrors('night_shift_start_at')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- WBS Codes Section -->
    <WbsCodeList :project-id="$props.projectId" />

    <!-- Cover Page Section -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div class="flex items-center space-x-3">
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-gray-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>

          <h3 class="text-lg font-semibold text-gray-900">
            {{ $t('ProjectSettings.ProjectTab.sections.coverPage') }}
          </h3>
        </div>
      </div>

      <div class="p-6">
        <div class="max-w-2xl mx-auto">
          <div class="text-center">
            <div class="mb-6">
              <FileUpload
                v-if="projectsStore.project?.unarchived"
                v-model="fileUploadForm"
                accept="image/jpeg,image/png,application/pdf"
                @change="fileUploaded"
              />
            </div>

            <div
              v-if="coverPageMedia.mediaCollection.value.length > 0"
              class="mt-6"
            >
              <MediaDisplay
                :media="coverPageMedia.mediaCollection.value[0]"
                class="max-w-md mx-auto"
                :allow-delete="project?.unarchived"
                @delete="coverPageMedia.removeMedia($event.uuid)"
              />
            </div>

            <div
              v-else
              class="text-center py-8"
            >
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="mx-auto h-12 w-12 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>

              <h3 class="mt-2 text-sm font-medium text-gray-900">
                No cover page uploaded
              </h3>

              <p class="mt-1 text-sm text-gray-500">
                Upload an image or PDF to set as the project cover page.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Project Documents Section -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div class="flex items-center space-x-3">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

          <h3 class="text-lg font-semibold text-gray-900">
            {{ $t('ProjectSettings.ProjectTab.sections.documents') }}
          </h3>
        </div>
      </div>

      <div class="p-6">
        <div class="max-w-4xl mx-auto">
          <div class="mb-6">
            <FileUpload
              v-if="projectsStore.project?.unarchived"
              v-model="documentsUploadForm"
              multiple
              @change="documentsUploaded"
            />
          </div>

          <div
            v-if="documentsMedia.mediaCollection.value.length > 0"
            class="mt-6 relative mb-10"
          >
            <button
              v-if="documentsMedia.mediaCollection.value.length > 1"
              class="absolute left-0 inset-y-1/2 border border-black rounded-full w-8 h-8 flex justify-center items-center hover:bg-gray-100 active:bg-gray-200 z-10 bg-white"
              type="button"
              @click="documentsSwiper?.slidePrev()"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <Swiper
              class="max-w-md mb-4"
              :space-between="20"
              :loop="documentsMedia.mediaCollection.value.length > 1"
              centered-slides
              :modules="[Pagination]"
              :pagination="{
                el: '.swiper-pagination',
                type: 'bullets'
              }"
              @swiper="setDocumentsSwiper"
              @slide-change="onDocumentsSlideChange"
            >
              <SwiperSlide
                v-for="(media, index) in documentsMedia.mediaCollection.value"
                :key="media.uuid"
                class="!h-auto"
              >
                <div class="flex items-center h-full w-full justify-center min-h-40">
                  <MediaDisplay
                    v-if="loadedDocumentIndexes.has(index)"
                    :media="media"
                    class="w-60"
                    :allow-delete="project?.unarchived"
                    @delete="documentsMedia.removeMedia($event.uuid)"
                    @refresh="documentsMedia.refreshMedia(media.uuid)"
                  />
                </div>
              </SwiperSlide>
            </Swiper>

            <div class="swiper-pagination !bottom-auto" />

            <button
              v-if="documentsMedia.mediaCollection.value.length > 1"
              class="absolute right-0 inset-y-1/2 border border-black rounded-full w-8 h-8 flex justify-center items-center hover:bg-gray-100 active:bg-gray-200 z-10 bg-white"
              type="button"
              @click="documentsSwiper?.slideNext()"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>
          </div>

          <div
            v-else
            class="text-center py-8"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mx-auto h-12 w-12 text-gray-400">
    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>

            <h3 class="mt-2 text-sm font-medium text-gray-900">
              {{ $t('ProjectSettings.ProjectTab.empty.documentsTitle') }}
            </h3>

            <p class="mt-1 text-sm text-gray-500">
              {{ $t('ProjectSettings.ProjectTab.empty.documentsDescription') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import MediaDisplay from '@/components/MediaDisplay.vue';
import WbsCodeList from '@/components/ProjectSettings/ProjectTab/WbsCodeList.vue';
import { useFormData } from '@/composables/useFormData';
import { useMediaCollection } from '@/composables/useMediaCollection';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { timeForInput } from '@/helpers';
import { t } from '@/i18n';
import { useProjectsStore } from '@/store/projects';
import Swal from 'sweetalert2';
import { Pagination } from 'swiper/modules';
import { Swiper as SwiperInterface } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { computed, nextTick, ref, watch } from 'vue';
import 'swiper/css/pagination';

const props = defineProps<{
  projectId: string;
}>();

const emits = defineEmits<{
  (e: 'dirty');
  (e: 'saved');
}>();

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.models.query().find(parseInt(props.projectId));
});

const projectForm = useFormData({
  name: '',
  labour_standard_shift: 0,
  plant_standard_shift: 0,
  maximum_shift: 0,
  shift_start_at: '',
  night_shift_start_at: '',
  track_weekends: false,
  reference: '',
  default_dashboard: '',
  description: '',
  business_role: '',
  locale: '',
});

watch(project, (newValue) => {
  if(newValue) {
    projectForm.setData(newValue, ['shift_start_at', 'night_shift_start_at']);
    projectForm.form.shift_start_at = timeForInput(newValue.shiftStartAt);
    projectForm.form.night_shift_start_at = timeForInput(newValue.nightShiftStartAt);

    nextTick(() => {
      projectForm.resetDirtyStatus();
    });
  }
}, { immediate: true });

watch(projectForm.isDirty, (newValue) => {
  if(newValue) {
    emits('dirty');
  } else {
    emits('saved');
  }
});

const updateProjectAction = useStoreApiAction(projectsStore.updateProject);

const updateProject = () => {
  projectForm.resetErrors();

  updateProjectAction.request(props.projectId, projectForm.form).then(() => {
    Swal.fire({
      title: t('ProjectSettings.ProjectTab.messages.projectUpdated'),
      icon: 'success',
    });

    // projectForm.form.name = project.value.name;
    // projectForm.form.labour_standard_shift = project.value.labour_standard_shift;
    // projectForm.form.plant_standard_shift = project.value.plant_standard_shift;
    // projectForm.form.maximum_shift = project.value.maximum_shift;
    projectForm.setData(project.value, ['shift_start_at', 'night_shift_start_at']);

    projectForm.form.shift_start_at = timeForInput(project.value.shiftStartAt);
    projectForm.form.night_shift_start_at = timeForInput(project.value.nightShiftStartAt);
  }).catch((error) => {
    projectForm.setErrors(error.data);
  });
};

const coverPageMedia = useMediaCollection(projectsStore.showCoverPage);
const documentsMedia = useMediaCollection(projectsStore.showDocuments);
const documentsSwiper = ref<SwiperInterface>();
const loadedDocumentIndexes = ref(new Set<number>([0]));

const setDocumentsSwiper = (swiper: SwiperInterface) => {
  documentsSwiper.value = swiper;
  markDocumentIndexLoaded(swiper.realIndex);
};

const onDocumentsSlideChange = (swiper: SwiperInterface) => {
  markDocumentIndexLoaded(swiper.realIndex);
};

const markDocumentIndexLoaded = (index: number) => {
  if(!loadedDocumentIndexes.value.has(index)) {
    loadedDocumentIndexes.value = new Set([...loadedDocumentIndexes.value, index]);
  }
};

watch(() => props.projectId, (newValue) => {
  if(newValue) {
    coverPageMedia.clear();
    coverPageMedia.fetchMedia(newValue);

    documentsMedia.clear();
    documentsMedia.fetchMedia(newValue);
    loadedDocumentIndexes.value = new Set([0]);
  }
}, { immediate: true });

const storeMediaAction = useStoreApiAction(projectsStore.storeCoverPage);
const storeDocumentsAction = useStoreApiAction(projectsStore.storeDocuments);
const fileUploadForm = ref();
const documentsUploadForm = ref();

const fileUploaded = (event: Event) => {
  const files = Array.from((event.target as HTMLInputElement).files);
  const selectedFile = files[0];

  if(selectedFile) {
    const fileFormData = new FormData();

    fileFormData.append('images[]', selectedFile);

    storeMediaAction.request(project.value.id, fileFormData).then((medias) => {
      coverPageMedia.clear();
      coverPageMedia.addMedia(medias);
      fileUploadForm.value = [];
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: t('ProjectSettings.ProjectTab.messages.uploadFailed'),
          text: error.data.message,
        });
      }
    });
  }
};

const documentsUploaded = (event: Event) => {
  const files = Array.from((event.target as HTMLInputElement).files);

  files.forEach((file) => {
    const fileFormData = new FormData();

    fileFormData.append('images[]', file);

    storeDocumentsAction.request(project.value.id, fileFormData).then((medias) => {
      documentsMedia.addMedia(medias);
      documentsUploadForm.value = [];
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: t('ProjectSettings.ProjectTab.messages.uploadFailed'),
          text: error.data.message,
        });
      }
    });
  });
};
</script>

<style scoped></style>
